import {
  getDesktopChatAuthScope,
  updateDesktopChatAuthScope,
  resolveDesktopChatAuthScope,
} from "@/desktop/desktop-chat-auth-scope";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { createElement } from "react";
import * as authApi from "@/api/auth";
import { useOnboardingStore } from "@/stores/onboarding-store";
import { useProjectStore } from "@/stores/project-store";
import { syncAppLanguageToAccount } from "@/stores/app-language-store";
import { translate } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import type { User } from "@/api/auth";
import type { ApiError } from "@/api/client";
import { usePrivacyConsentSync } from "./use-privacy-consent-sync";
import {
  analyticsAllowed,
  getPrivacyAccount,
  analyticsEpoch,
  setPrivacyAccount,
  usePrivacyConsent,
} from "@/lib/privacy-consent";
import { onPostHogReady, posthog } from "@/lib/posthog";
import { isTauri } from "@/lib/is-tauri";
import { resetToastCooldowns } from "@/lib/toast-cooldown-gate";
import { invoke } from "@tauri-apps/api/core";
import { useWindowRole } from "@/desktop/use-window-role";
import { setOnboardingLocalStateUser } from "@/lib/onboarding-local-state";
import {
  clearSignupAttribution,
  type SignupAttribution,
} from "@/lib/signup-attribution";

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (data: {
    name: string;
    email: string;
    password1: string;
    password2: string;
    newsletter_consent?: boolean;
    signup_attribution?: SignupAttribution;
    attribution_token?: string;
  }) => Promise<void>;
  logout: (opts?: { skipServer?: boolean }) => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);
const DESKTOP_IDENTITY_EVENT = "desktop_user_identified";
const DESKTOP_IDENTITY_KEY_PREFIX = "denker.desktop.identity_tracked";

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const windowRole = useWindowRole();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Consecutive user loads that failed on the network or a server error;
  // each one schedules the next retry.
  const [loadFailures, setLoadFailures] = useState(0);
  // Only the latest load (or a logout) may settle the user.
  const loadSeq = useRef(0);
  const [posthogReady, setPosthogReady] = useState(() => posthog.__loaded);
  usePrivacyConsent();
  const analyticsConsented = analyticsAllowed();
  usePrivacyConsentSync(user?.id ?? null);

  const refreshUser = useCallback(async () => {
    const seq = ++loadSeq.current;
    try {
      const u = await authApi.getUser();
      if (seq !== loadSeq.current) return;
      if (!isTauri())
        updateDesktopChatAuthScope(
          resolveDesktopChatAuthScope(String(u.id), true),
        );
      setPrivacyAccount(String(u.id), u.analytics_excluded !== false);
      setUser(u);
      setLoadFailures(0);
    } catch (error) {
      if (seq !== loadSeq.current) return;
      const status = (error as ApiError).status;
      if (!isTauri()) updateDesktopChatAuthScope(null);
      setPrivacyAccount(status === 401 ? null : undefined);
      setUser(null);
      const transient = status === undefined || status >= 500;
      setLoadFailures((failures) => (transient ? failures + 1 : 0));
    }
  }, []);

  useEffect(() => {
    refreshUser().finally(() => setIsLoading(false));
  }, [refreshUser]);

  // A network blip or server error must not strand a signed-in session
  // without its user (onboarding never hydrates); retry with backoff.
  useEffect(() => {
    if (loadFailures === 0) return;
    const delay = Math.min(1000 * 2 ** (loadFailures - 1), 30_000);
    const timer = setTimeout(() => void refreshUser(), delay);
    return () => clearTimeout(timer);
  }, [loadFailures, refreshUser]);

  useEffect(() => {
    if (!analyticsAllowed()) {
      setPosthogReady(false);
      return;
    }
    return onPostHogReady(() => setPosthogReady(true));
  }, [analyticsConsented]);

  // The app language lives on this machine; the account learns it so agents
  // reply in it. Only the owner window talks for the account.
  const userId = user?.id;
  const accountLanguage = user?.language;
  useEffect(() => {
    if (!userId || windowRole !== "owner") return;
    syncAppLanguageToAccount(accountLanguage);
  }, [userId, accountLanguage, windowRole]);

  const login = useCallback(
    async (email: string, password: string) => {
      setPrivacyAccount(undefined);
      await authApi.login({ email, password });
      resetToastCooldowns();
      await refreshUser();
    },
    [refreshUser],
  );

  // Identify user in PostHog when authenticated. Desktop starts as an
  // install-scoped device identity, then switches to the authenticated user.
  // Emit an owner-only authenticated event after identify so desktop usage can
  // be analyzed by email even when earlier pre-auth events stay anonymous.
  useEffect(() => {
    if (!user || !posthogReady || !analyticsAllowed()) return;
    const userId = String(user.id);
    const userProperties = {
      email: user.email,
      name: user.name,
    };

    if (!isTauri()) {
      posthog.identify(userId, userProperties);
      return;
    }

    let cancelled = false;
    const identityEpoch = analyticsEpoch();
    const identityCurrent = () =>
      !cancelled &&
      analyticsAllowed() &&
      analyticsEpoch() === identityEpoch &&
      getPrivacyAccount() === userId;
    void invoke<string>("device_id")
      .then(async (deviceId) => {
        if (!identityCurrent()) return;
        const identityKey = `${DESKTOP_IDENTITY_KEY_PREFIX}.${userId}.${deviceId || "unknown"}`;
        const firstIdentityForDevice =
          deviceId &&
          deviceId !== userId &&
          localStorage.getItem(identityKey) !== "1";

        if (firstIdentityForDevice) {
          // PostHog aliases link the current anonymous/device distinct_id to
          // the future user distinct_id. They are not retroactive, so this
          // must happen before identify; the explicit event below makes future
          // desktop usage queryable by email.
          posthog.alias(userId);
        }

        posthog.identify(userId, userProperties);

        const { getWindowRoleSync } = await import("@/desktop/use-window-role");
        if (!identityCurrent()) return;
        if (getWindowRoleSync() === "owner") {
          posthog.capture(DESKTOP_IDENTITY_EVENT, {
            user_id: userId,
            email: user.email,
            name: user.name,
            device_id: deviceId,
          });
        }

        if (firstIdentityForDevice) {
          localStorage.setItem(identityKey, "1");
        }
      })
      .catch((e) => {
        if (!identityCurrent()) return;
        posthog.identify(userId, userProperties);
        console.warn("[auth] desktop identity tracking failed:", e);
      });
    return () => {
      cancelled = true;
    };
  }, [posthogReady, user]);

  // Initialize onboarding store for the authenticated user and rehydrate it
  // when the backend step changes after a same-session onboarding reset.
  const onboardingHydrationRef = useRef<{
    userId: string;
    onboardingStep: string | null;
    windowRole: typeof windowRole;
  } | null>(null);
  const onboardingRoleRef = useRef(windowRole);
  const ownerPromotionRef = useRef(false);
  useEffect(() => {
    if (onboardingRoleRef.current !== windowRole) {
      ownerPromotionRef.current = windowRole === "owner";
      onboardingRoleRef.current = windowRole;
    }
    if (!user) {
      if (onboardingHydrationRef.current !== null) {
        onboardingHydrationRef.current = null;
        useOnboardingStore.getState().clearUser();
      }
      return;
    }

    const current = {
      userId: user.id,
      onboardingStep: user.onboarding_step,
      windowRole,
    };
    const previous = onboardingHydrationRef.current;
    if (
      !previous ||
      previous.userId !== current.userId ||
      previous.onboardingStep !== current.onboardingStep ||
      previous.windowRole !== windowRole
    ) {
      if (!previous || previous.userId !== current.userId) {
        // Before any per-user hydration: discard a persisted project/space
        // selection stamped by a DIFFERENT account. Auth state can change
        // without a sign-out (external token change, server-side account
        // recreation) — restoring the other account's selection 404s with
        // "project not found" toasts on boot.
        useProjectStore.getState().adoptSelectionOwner(user.id);
      }
      onboardingHydrationRef.current = current;
      // A promoted mirror may already hold a newer owner snapshot than its
      // original REST response, even if that response was still pending when
      // ownership moved. Preserve only a snapshot for the verified account.
      const preserveOwnerSnapshot =
        ownerPromotionRef.current &&
        windowRole === "owner" &&
        getDesktopChatAuthScope()?.accountId === user.id &&
        useOnboardingStore.getState().stepHydrated;
      ownerPromotionRef.current = false;
      if (preserveOwnerSnapshot) {
        setOnboardingLocalStateUser(user.id, "desktop");
        return;
      }
      useOnboardingStore
        .getState()
        .initFromUser(
          user.onboarding_step,
          user.seen_tips ?? [],
          user.id,
          user.desktop_onboarding_version,
        );
    }
  }, [user, windowRole]);

  const register = useCallback(
    async (data: {
      name: string;
      email: string;
      password1: string;
      password2: string;
      newsletter_consent?: boolean;
      signup_attribution?: SignupAttribution;
      attribution_token?: string;
    }) => {
      await authApi.register(data);
      clearSignupAttribution();
    },
    [],
  );

  const doLogout = useCallback(async (opts?: { skipServer?: boolean }) => {
    setPrivacyAccount(undefined);
    if (!isTauri()) updateDesktopChatAuthScope(null);
    // Desktop: delegate to the shared teardown routine (companion stop,
    // workspace geometry collapse, server revocation, Keychain clear,
    // cross-webview broadcast). Web logout has no overlay state to tear
    // down — the AuthGuard route boundary handles redirect.
    if (isTauri()) {
      try {
        const { desktopSignOut } = await import("@/desktop/desktop-sign-out");
        await desktopSignOut({ skipServer: opts?.skipServer });
      } catch (err) {
        console.warn("[auth] desktop logout teardown failed:", err);
      }
    } else if (!opts?.skipServer) {
      try {
        await authApi.logout();
      } catch {
        // Best-effort: the server session may already be invalid (account
        // deletion logs out server-side before this runs) — the local
        // teardown below must still complete so the UI doesn't strand in
        // a signed-in shell.
      }
    }
    resetToastCooldowns();
    // Drop the persisted project/space selection so the next account on
    // this browser (or a re-created account) never boots against IDs that
    // no longer exist server-side. desktopSignOut() also clears (tray
    // path bypasses doLogout) — double-run here is an idempotent no-op.
    const { useProjectStore } = await import("@/stores/project-store");
    useProjectStore.getState().clear();
    setPrivacyAccount(isTauri() ? undefined : null);
    loadSeq.current += 1;
    setUser(null);
    setLoadFailures(0);
    if (isTauri()) {
      const { track } = await import("@/desktop/observability");
      track("desktop_sign_out");
    }
    if (posthog.__loaded) posthog.reset();
  }, []);

  const value: AuthContextValue = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    register,
    logout: doLogout,
    refreshUser,
  };

  return createElement(AuthContext.Provider, { value }, children);
}

/**
 * Extract a user-friendly error message from an API error.
 */
function isApiError(err: unknown): err is ApiError {
  return (
    typeof err === "object" && err !== null && "status" in err && "data" in err
  );
}

export function getAuthErrorMessage(err: unknown): string {
  const fallback = translate(commonMessages, "somethingWentWrongRetry");
  if (!isApiError(err) || !err.data) return fallback;

  const data = err.data;
  // dj-rest-auth returns errors in various formats
  if (typeof data.detail === "string") return data.detail;
  if (
    typeof data.non_field_errors === "object" &&
    Array.isArray(data.non_field_errors)
  ) {
    return (data.non_field_errors as string[]).join(" ");
  }
  // Field-level errors: { email: ["..."], password1: ["..."] }
  const messages: string[] = [];
  for (const [, value] of Object.entries(data)) {
    if (Array.isArray(value)) {
      messages.push(...(value as string[]));
    } else if (typeof value === "string") {
      messages.push(value);
    }
  }
  return messages.join(" ") || fallback;
}
